"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslations } from "next-intl";
import { type PropsWithChildren, useMemo } from "react";
import { Controller, type Resolver, useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useMediaQuery } from "@/lib/hooks/use-media-query";
import type { Product } from "@/lib/schemas/paycode/product";
import { useCheckoutStore } from "@/lib/store/checkout";
import { cn } from "@/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from "../ui/drawer";
import { Label } from "../ui/label";

type ProductFormProps = {
  products: Product[];
  onDone: () => void;
};

type ProductsTranslator = ReturnType<typeof useTranslations<"products">>;

const buildSchema = (products: Product[], t: ProductsTranslator) => {
  const quantitySchema = z
    .string()
    .refine(
      (v) => /^\d+$/.test(v) && Number(v) >= 1,
      t("errors.quantityInvalid"),
    );

  const amountSchema = z
    .string()
    .refine(
      (v) => /^\d+(\.\d{1,2})?$/.test(v) && Number(v) > 0,
      t("errors.amountInvalid"),
    );

  const shape: Record<string, z.ZodTypeAny> = {};
  for (const product of products) {
    if (product.editableQuantity) shape[`q-${product.id}`] = quantitySchema;
    if (product.editableAmount) shape[`a-${product.id}`] = amountSchema;
  }
  return z.object(shape);
};

export const EditProductsButton = ({
  className,
  children,
}: PropsWithChildren<{ className?: string }>) => {
  const t = useTranslations("products");
  const setOpen = useCheckoutStore((s) => s.setEditProductsOpen);
  const hasEditable = useCheckoutStore((s) =>
    s.products.some((p) => p.editableQuantity || p.editableAmount),
  );

  if (!hasEditable) return null;

  return (
    <button
      type="button"
      onClick={() => setOpen(true)}
      className={cn(
        "cursor-pointer text-accent text-sm hover:opacity-80",
        className,
      )}
    >
      {children ?? t("editProducts")}
    </button>
  );
};

export const EditProducts = () => {
  const t = useTranslations("products");
  const open = useCheckoutStore((s) => s.editProductsOpen);
  const setOpen = useCheckoutStore((s) => s.setEditProductsOpen);

  const products = useCheckoutStore((s) => s.products);
  const isDesktop = useMediaQuery("(min-width: 64rem)");

  if (isDesktop)
    return (
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>{t("editProducts")}</DialogTitle>
            <DialogDescription>{t("editDescription")}</DialogDescription>
          </DialogHeader>
          <ProductsForm products={products} onDone={() => setOpen(false)} />
        </DialogContent>
      </Dialog>
    );

  return (
    <Drawer open={open} onOpenChange={setOpen}>
      <DrawerContent>
        <DrawerHeader className="text-left">
          <DrawerTitle>{t("editProducts")}</DrawerTitle>
          <DrawerDescription>{t("editDescription")}</DrawerDescription>
        </DrawerHeader>
        <div className="px-4 pb-4">
          <ProductsForm products={products} onDone={() => setOpen(false)} />
        </div>
      </DrawerContent>
    </Drawer>
  );
};

const ProductsForm = ({ products, onDone }: ProductFormProps) => {
  const t = useTranslations("products");
  const setProducts = useCheckoutStore((s) => s.setProducts);

  const editable = useMemo(
    () => products.filter((p) => p.editableQuantity || p.editableAmount),
    [products],
  );

  const schema = useMemo(() => buildSchema(editable, t), [editable, t]);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<Record<string, string>>({
    resolver: zodResolver(schema) as Resolver<Record<string, string>>,
    mode: "onSubmit",
    defaultValues: Object.fromEntries(
      editable.flatMap((p) => [
        [`q-${p.id}`, String(p.quantity)],
        [`a-${p.id}`, String(p.amount)],
      ]),
    ),
  });

  const onSubmit = (data: Record<string, string>) => {
    setProducts(
      products.map((p) => ({
        ...p,
        quantity: p.editableQuantity ? Number(data[`q-${p.id}`]) : p.quantity,
        amount: p.editableAmount ? Number(data[`a-${p.id}`]) : p.amount,
      })),
    );
    onDone();
  };

  return (
    <form className="flex flex-col gap-4" onSubmit={handleSubmit(onSubmit)}>
      {editable.map((product) => (
        <div key={product.id} className="flex flex-col gap-1.5">
          <span className="truncate font-medium text-sm">{product.name}</span>
          <div className="flex gap-2">
            <div className="flex flex-1 flex-col gap-1.5">
              <Label htmlFor={`q-${product.id}`}>{t("quantity")}</Label>
              {product.editableQuantity ? (
                <Controller
                  control={control}
                  name={`q-${product.id}`}
                  render={({ field }) => (
                    <Input
                      id={`q-${product.id}`}
                      inputMode="numeric"
                      aria-invalid={!!errors[`q-${product.id}`]}
                      {...field}
                      onChange={(e) =>
                        field.onChange(e.target.value.replace(/\D/g, ""))
                      }
                    />
                  )}
                />
              ) : (
                <span className="py-2 text-muted text-sm tabular-nums">
                  {product.quantity}
                </span>
              )}
            </div>
            <div className="flex flex-1 flex-col gap-1.5">
              <Label htmlFor={`a-${product.id}`}>{t("amount")}</Label>
              {product.editableAmount ? (
                <Controller
                  control={control}
                  name={`a-${product.id}`}
                  render={({ field }) => (
                    <Input
                      id={`a-${product.id}`}
                      inputMode="decimal"
                      aria-invalid={!!errors[`a-${product.id}`]}
                      {...field}
                      onChange={(e) =>
                        field.onChange(e.target.value.replace(/[^\d.]/g, ""))
                      }
                    />
                  )}
                />
              ) : (
                <span className="py-2 text-muted text-sm tabular-nums">
                  {product.amount.toLocaleString("es-MX", {
                    style: "currency",
                    currency: "MXN",
                  })}
                </span>
              )}
            </div>
          </div>
          {(errors[`q-${product.id}`] || errors[`a-${product.id}`]) && (
            <p className="text-destructive text-sm">
              {errors[`q-${product.id}`]?.message ??
                errors[`a-${product.id}`]?.message}
            </p>
          )}
        </div>
      ))}
      <Button type="submit" className="self-center">
        {t("save")}
      </Button>
    </form>
  );
};
